import React from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import { OrderActionsProps, ShippingStatus } from "./Order.types";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import OrderStatus from "./OrderStatus";

const useStyles = (theme: Theme) => ({
  OrderActionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  OrderActionSingleItemTopPaddingRowContainer: {
    pt: 2,
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  OrderActionSingleItemRowContainer: {
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  OrderActionContentFullWidthTextFieldContainer: {
    width: "100%",
    padding: 1,
  },
  OrderActionContentFullWidthTextField: {
    width: "100%",
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
});

export const OrderRequestedActions: React.FC<OrderActionsProps> = (
  props
): React.JSX.Element => {
  const { orderItemAction, multipleItems, onOrderItemActionUpdate, styles } = props;

  logDebug("OrderRequestedActions PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("OrderRequestedActions effectiveStyles");
  logDebug(effectiveStyles);

  const handleTrackingNumberValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.trackingNumber = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleNoteValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.note = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleIssueRefundValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.issueRefund = !newOrderItemAction.issueRefund;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    selection: ShippingStatus
  ) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.shippingStatus = selection;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const children = [
    <ToggleButton
      value={ShippingStatus.REQUESTED}
      key={ShippingStatus.REQUESTED}
    >
      <OrderStatus
        status={ShippingStatus.REQUESTED}
        isAdmin={true}
        displayChipForZeroCount
        disabled={orderItemAction.shippingStatus !== ShippingStatus.REQUESTED}
        styles={effectiveStyles}
      />
    </ToggleButton>,
    <ToggleButton
      value={ShippingStatus.LABELS_SHIPPED}
      key={ShippingStatus.LABELS_SHIPPED}
    >
      <OrderStatus
        status={ShippingStatus.LABELS_SHIPPED}
        isAdmin={true}
        label="Ship Labels"
        displayChipForZeroCount
        disabled={
          orderItemAction.shippingStatus !== ShippingStatus.LABELS_SHIPPED
        }
        styles={effectiveStyles}
      />
    </ToggleButton>,
    <ToggleButton value={ShippingStatus.REJECTED} key={ShippingStatus.REJECTED}>
      <OrderStatus
        status={ShippingStatus.REJECTED}
        isAdmin={true}
        label="Reject"
        displayChipForZeroCount
        disabled={orderItemAction.shippingStatus !== ShippingStatus.REJECTED}
        styles={effectiveStyles}
      />
    </ToggleButton>,
  ];

  const control = {
    value: orderItemAction.shippingStatus,
    onChange: handleChange,
    exclusive: true,
  };

  return (
    <>
      <Grid sx={effectiveStyles.OrderActionContainer}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ToggleButtonGroup size="small" {...control} aria-label="Small sizes">
            {children}
          </ToggleButtonGroup>
        </Stack>
        {orderItemAction.shippingStatus === ShippingStatus.LABELS_SHIPPED && (
          <Grid
            className="OrderActionSingleItemTopPaddingRowContainer"
            sx={effectiveStyles.OrderActionSingleItemTopPaddingRowContainer}
          >
            <Grid
              className="OrderActionContentFullWidthTextFieldContainer"
              sx={effectiveStyles.OrderActionContentFullWidthTextFieldContainer}
            >
              <TextField
                className="OrderActionContentFullWidthTextField"
                sx={effectiveStyles.OrderActionContentFullWidthTextField}
                id="trackingNumber"
                label="Shipping Tracking Number"
                value={orderItemAction.trackingNumber || ""}
                onChange={handleTrackingNumberValueChange}
              />
            </Grid>
          </Grid>
        )}
        {orderItemAction.shippingStatus !== ShippingStatus.REQUESTED && (
          <Grid
            className="OrderActionSingleItemTopPaddingRowContainer"
            sx={effectiveStyles.OrderActionSingleItemTopPaddingRowContainer}
          >
            <Grid
              className="OrderActionContentFullWidthTextFieldContainer"
              sx={effectiveStyles.OrderActionContentFullWidthTextFieldContainer}
            >
              <TextField
                className="OrderActionContentFullWidthTextField"
                sx={effectiveStyles.OrderActionContentFullWidthTextField}
                id="note"
                label="Note"
                value={orderItemAction.note || ""}
                onChange={handleNoteValueChange}
              />
            </Grid>
          </Grid>
        )}
        {orderItemAction.shippingStatus === ShippingStatus.REJECTED && (
          <Grid
            className="OrderActionSingleItemRowContainer"
            sx={effectiveStyles.OrderActionSingleItemRowContainer}
          >
            <Grid
              className="OrderActionContentFullWidthTextFieldContainer"
              sx={effectiveStyles.OrderActionContentFullWidthTextFieldContainer}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={orderItemAction.issueRefund}
                    onChange={handleIssueRefundValueChange}
                  />
                }
                label="Issue Item Refund"
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default OrderRequestedActions;
